import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import { useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import proxy from '@/services/ProxyService';
import { LoadingButton } from '@mui/lab';
import UserSelector from '@/components/selectors/UserSelector';

const getCaretCoordinates = (element, position) => {
  const div = document.createElement('div');
  const style = window.getComputedStyle(element);
  Array.from(style).forEach((prop) => {
    div.style[prop] = style.getPropertyValue(prop);
  });
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.width = `${element.clientWidth}px`;
  div.textContent = element.value.substring(0, position);
  const span = document.createElement('span');
  span.textContent = '|';
  div.appendChild(span);
  document.body.appendChild(div);
  const { offsetLeft: spanLeft, offsetTop: spanTop } = span;
  document.body.removeChild(div);
  const rect = element.getBoundingClientRect();
  const top = rect.top + spanTop - element.scrollTop;
  const left = rect.left + spanLeft;
  return { top: top + 24, left: left };
};

const SupportTicketDialog = ({
  itemUuid,
  rows,
  handleCloseDialog,
  contract,
  isSalesBOMItem,
  addLinkedDocumentToContract,
}) => {
  const {
    me,
    fixedDeploymentConfigs: { contracts },
  } = useRouteLoaderData('auth');
  const [selectedUser, setSelectedUser] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [important, setImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [replaceRange, setReplaceRange] = useState(null);
  const subjectRef = useRef(null);
  const descriptionRef = useRef(null);

  const TEXT_VARIABLES = useMemo(() => {
    if (!contract) return {};
    const item = isSalesBOMItem
      ? rows[itemUuid.parentIndex]?.salesBillOfMaterialItems[itemUuid.subIndex]
      : rows.find((r) => r.uuid === itemUuid);

    return {
      LINK: `${import.meta.env.VITE_LIVE_URL}billing/contracts/${contract._id}`,
      POSITION_TITEL: item ? item.title : '',
      POSITION_BESCHREIBUNG: item ? item.description : '',
      POSITION_ARTIKEL_NUMMER: item ? item.articleNumber : '',
      VERTRAGSNUMMER: contract.contractNumber,
      KUNDENNAME: contract.partyName,
      ZUGEWIESENER_MITARBEITER: selectedUser
        ? `${selectedUser.firstName} ${selectedUser.lastName}`
        : '',
      MEIN_NAME: `${me.firstName} ${me.lastName}`,
    };
  }, [rows, contract, me, selectedUser]);

  useEffect(() => {
    if (!itemUuid || !rows || rows.length === 0) return;
    const item = isSalesBOMItem
      ? rows[itemUuid.parentIndex]?.salesBillOfMaterialItems[itemUuid.subIndex]
      : rows.find((r) => r.uuid === itemUuid);
    if (!item) return;

    setSubject(`Nachfrage zum Vertrag: ${contract.contractNumber}`);
    let desc = `Es stellt sich mir die Frage zu folgendem Vertragsposten:\n\n`;
    if (isSalesBOMItem)
      desc += `Verkaufsstückliste: ${rows[itemUuid.parentIndex]?.title}\n`;
    desc += `Vertragsposten: ${item.title}\n`;
    desc += `Vertragsnummer: ${contract.contractNumber} ( {{ LINK }} )\n`;
    desc += `Kunde: ${contract.partyName}\n\n`;
    desc += `Bitte um Rückmeldung.\n\nMit freundlichen Grüßen\n${me.firstName} ${me.lastName}`;
    setDescription(desc);
  }, [itemUuid, rows, contract]);

  const handleFieldInteraction = (evt, fieldName) => {
    const input = evt.target;
    const cursor = input.selectionStart;
    const text = input.value;

    if (fieldName === 'subject') setSubject(text);
    else setDescription(text);
    setActiveField(fieldName);
    let foundRange = null;

    if (cursor >= 2 && text.slice(cursor - 2, cursor) === '{{') {
      foundRange = { start: cursor - 2, end: cursor };
    } else {
      const lastOpen = text.lastIndexOf('{{', cursor);
      const nextClose = text.indexOf('}}', cursor);
      if (lastOpen !== -1 && nextClose !== -1 && nextClose > lastOpen) {
        if (!text.slice(lastOpen, nextClose + 2).includes('\n')) {
          foundRange = { start: lastOpen, end: nextClose + 2 };
        }
      }
    }

    if (foundRange) {
      setReplaceRange(foundRange);
      const coords = getCaretCoordinates(input, foundRange.end);
      setMenuPosition(coords);
    } else {
      setMenuPosition(null);
      setReplaceRange(null);
    }
  };

  const insertVariable = (key) => {
    if (!replaceRange || !activeField) return;
    const currentText = activeField === 'subject' ? subject : description;
    const setText = activeField === 'subject' ? setSubject : setDescription;
    const ref = activeField === 'subject' ? subjectRef : descriptionRef;
    const textBefore = currentText.slice(0, replaceRange.start);
    const textAfter = currentText.slice(replaceRange.end);
    const newTag = `{{ ${key} }}`;
    const newText = textBefore + newTag + textAfter;

    setText(newText);
    setMenuPosition(null);
    setReplaceRange(null);
    setTimeout(() => {
      if (ref.current) {
        ref.current.focus();
        const newCursorPos = replaceRange.start + newTag.length;
        ref.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const replaceVariables = (text) => {
    let processedText = text;
    Object.keys(TEXT_VARIABLES).forEach((key) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      processedText = processedText.replace(regex, TEXT_VARIABLES[key]);
    });
    return processedText;
  };

  const createSupportTicket = async () => {
    setLoading(true);
    const finalSubject = replaceVariables(subject);
    const finalDescription = replaceVariables(description);

    try {
      const ticket = {
        companyId: '100000',
        title: finalSubject,
        content: finalDescription,
        remitterId: me.tanssEmployeeId ?? null,
        ...(selectedUser && {
          assignedToEmployeeId: Number(selectedUser.tanssEmployeeId),
        }),
        ...(important && { tags: contracts.importantTags }),
      };
      const {
        data: { content },
      } = await proxy.post(`/${contracts.interfaces.tanss}/tickets`, ticket);
      addLinkedDocumentToContract({
        type: 'TANSS',
        id: '' + content.id,
        collection: 'tickets',
        title: ticket.title,
        description: ticket.content,
      });
      toast.update(
        toast.success(
          <ToastMessage title={`Ticket #${content.id} erfolgreich erstellt`} />,
          {
            autoClose: 60000,
            closeOnClick: false,
            closeButton: true,
          },
        ),
      );
      handleCloseDialog();
    } catch (error) {
      toast.error(<ToastMessage title="Fehler" subtitle={error.message} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>Ticket erstellen</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
      >
        <UserSelector user={selectedUser} setUser={(u) => setSelectedUser(u)} />

        <TextField
          label="Betreff"
          fullWidth
          value={subject}
          inputRef={subjectRef}
          onChange={(e) => handleFieldInteraction(e, 'subject')}
          onClick={(e) => handleFieldInteraction(e, 'subject')}
          onKeyUp={(e) => handleFieldInteraction(e, 'subject')}
          InputProps={{
            endAdornment: (
              <ToggleButton
                selected={important}
                value="check"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setImportant(!important);
                }}
                size="small"
                color="primary"
              >
                WICHTIG
              </ToggleButton>
            ),
          }}
        />

        <TextField
          label="Beschreibung"
          fullWidth
          multiline
          value={description}
          rows={7}
          inputRef={descriptionRef}
          onChange={(e) => handleFieldInteraction(e, 'description')}
          onClick={(e) => handleFieldInteraction(e, 'description')}
          onKeyUp={(e) => handleFieldInteraction(e, 'description')}
          helperText="Klicken Sie in {{ ... }} Bereiche, um Variablen zu ändern."
        />

        <Menu
          open={Boolean(menuPosition)}
          onClose={() => setMenuPosition(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            menuPosition
              ? { top: menuPosition.top, left: menuPosition.left }
              : undefined
          }
          PaperProps={{ style: { maxHeight: 200, width: '30ch' } }}
        >
          {Object.keys(TEXT_VARIABLES).map((key) => (
            <MenuItem key={key} onClick={() => insertVariable(key)}>
              <ListItemText
                primary={`{{ ${key} }}`}
                secondary={
                  TEXT_VARIABLES[key].length > 30
                    ? TEXT_VARIABLES[key].substring(0, 30) + '...'
                    : TEXT_VARIABLES[key]
                }
              />
            </MenuItem>
          ))}
        </Menu>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} color="inherit">
          Abbrechen
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={createSupportTicket}
          loading={loading}
        >
          Übertragen
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SupportTicketDialog;
